/**
 * Custom input handlers for MembersScreen
 */

// Custom firstName input handler to only allow valid name characters
export const createNameInputHandler = (fieldName, handleInputChange) => (e) => {
  const { value } = e.target;

  // Allow only letters, spaces, hyphens, apostrophes, and periods
  const namePattern = /^[a-zA-Z\s\-'.]*$/;

  if (namePattern.test(value)) {
    handleInputChange({
      target: {
        name: fieldName,
        value: value,
      },
    });
  }
};

// Custom ZIP input handler with country-based validation
export const createZipInputHandler = (search, handleInputChange) => (e) => {
  const { value } = e.target;
  const country = search.country;

  let isValid = false;
  let filteredValue = value;

  if (country === "USA") {
    // USA: Accept numeric values only (ZIP code format), max 5 digits
    filteredValue = value.replace(/[^0-9]/g, "").slice(0, 5);
    isValid = /^[0-9]*$/.test(filteredValue);
  } else if (country === "CAN") {
    // Canada: Accept alphanumeric and space (Canadian postal code format A1A 1A1)
    let cleanValue = value.replace(/[^a-zA-Z0-9\s]/g, "").toUpperCase();

    if (cleanValue.length > 0) {
      let noSpaces = cleanValue.replace(/\s/g, "");

      if (noSpaces.length <= 6) {
        if (noSpaces.length > 3) {
          filteredValue = noSpaces.slice(0, 3) + " " + noSpaces.slice(3);
        } else {
          filteredValue = noSpaces;
        }

        isValid = /^[A-Z]?([0-9][A-Z]?(\s[0-9]?[A-Z]?[0-9]?)?)?$/.test(
          filteredValue
        );
      } else {
        isValid = false;
      }
    } else {
      filteredValue = "";
      isValid = true;
    }
  } else {
    // Default: allow any input for other countries
    isValid = true;
  }

  if (isValid) {
    handleInputChange({
      target: {
        name: "zip",
        value: filteredValue,
      },
    });
  }
};

// Enhanced handleInputChange to handle country changes and clear ZIP if needed
export const createCountryInputHandler = (search, handleInputChange) => (e) => {
  const { name, value } = e.target;

  if (name === "country") {
    const currentZip = search.zip || "";
    let shouldClearZip = false;

    if (value === "USA") {
      shouldClearZip = /[^0-9]/.test(currentZip);
    } else if (value === "CAN") {
      shouldClearZip = /[^a-zA-Z0-9\s]/.test(currentZip);
    }

    if (shouldClearZip) {
      handleInputChange({
        target: {
          name: "zip",
          value: "",
        },
      });
    }
  }

  handleInputChange(e);
};

// Numeric-only input handler
export const createNumericInputHandler = (
  fieldName,
  maxLength,
  handleInputChange
) => (e) => {
  const value = e.target.value.replace(/\D/g, "");
  const limitedValue = maxLength ? value.slice(0, maxLength) : value;

  handleInputChange({
    target: {
      name: fieldName,
      value: limitedValue,
    },
  });
};

// Email input handler with basic validation
export const createEmailInputHandler = (handleInputChange, setEmailError) => (
  e
) => {
  const value = e.target.value;

  // Clear error when user edits
  if (setEmailError) {
    setEmailError("");
  }

  // Basic email validation pattern
  const emailPattern = /^[^\s@]*@?[^\s@]*$/;

  if (value === "" || emailPattern.test(value)) {
    handleInputChange({
      target: {
        name: "email",
        value: value,
      },
    });
  }
};

// Phone number input handler with error clearing
export const createPhoneInputHandler = (
  fieldName,
  setError,
  handleInputChange
) => (e) => {
  // Clear error when user edits
  if (setError) {
    setError("");
  }

  // Only allow numeric input
  const value = e.target.value.replace(/\D/g, "");
  handleInputChange({
    target: {
      name: fieldName,
      value: value,
    },
  });
};

// SIN input handler with error clearing
export const createSinInputHandler = (setError, handleInputChange) => (e) => {
  // Clear error when user edits
  if (setError) {
    setError("");
  }

  // Only allow numeric input
  const value = e.target.value.replace(/\D/g, "");
  const maxLength = 9;
  const limitedValue = value.slice(0, maxLength);

  handleInputChange({
    target: {
      name: "sin4",
      value: limitedValue,
    },
  });
};
